import { OptionBase } from './common';
import { DialogActionType } from './dialog';
import { IntegrationType } from './integration';
import { AuthConfig } from './node';

type BaseAction<T extends DialogActionType> = { action_id: string; type: T };

export type WebhookAction = BaseAction<'webhook'> & { webhook_id: string; fallback: Action[] };

export type TextAction = BaseAction<'text'> & {
  texts: string[];
  passthrough?: ChannelPassthrough[];
  options?: { label: string; text: string; option_id: string }[];
};

export type HandoverAction = BaseAction<'handover'> & {
  external: boolean;
  department_id: string;
  name: string;
  metadata?: {
    voice_handover_destination?: string;
    voice_handover_attributes?: Record<string, string>;
    voice_handover_transport?: 'dial' | 'refer';
  };
};

export type UrlAction = BaseAction<'url'> & { url: string; name?: string };

export type ImageAction = BaseAction<'image'> & { url: string; name: string };

export type VideoAction = BaseAction<'video'> & { url: string; name: string; size: number };

export type FileAction = BaseAction<'file'> & { url: string; name: string; size: number };

export type WebviewAction = BaseAction<'webview'> & {
  url: string;
  name: string;
  label: string;
  height: string;
  trigger_node_id: string;
  auto_open?: boolean;
};

export type CarouselAction = BaseAction<'carousel'> & {
  cards: CarouselCard[];
  passthrough?: ChannelPassthrough[];
};

export type ResetActionOption = { value: 'all' | 'custom'; label_key: string; disabled: boolean };

export type ResetAction = BaseAction<'reset'> & {
  nodeId?: string;
  name?: string;
  option: 'all' | 'specific';
  variables?: string[];
  autoCompleteValue?: string;
};

export type CloseAction = BaseAction<'close'>;

// The channels a switch_channel action can send the visitor to; the orchestrator mints links for these only
export const SWITCH_CHANNEL_DESTINATIONS = ['web'] as const;

export type SwitchChannelDestination = (typeof SWITCH_CHANNEL_DESTINATIONS)[number];

export type SwitchChannelAction = BaseAction<'switch_channel'> & {
  channel: SwitchChannelDestination;
  integration_id?: string;
};

export type ReplayAction = BaseAction<'replay'> & { texts?: string[] };

export type TagAction = BaseAction<'tag'> & { tags: string[]; operation?: TagOperation };

export type EventAction = BaseAction<'event'> & { trigger_node_id: string; node_id: string };
export type PauseAction = BaseAction<'pause'> & { duration: number; show_typing: boolean };
export type EmailAction = BaseAction<'email'> & {
  from: string;
  to: string[];
  subject: string;
  body: string;
};

export type GooglesheetAction = BaseAction<'googlesheet'> & {
  spreadsheet_id: string;
  sheet_id: string;
  // TODO: uncomment when merging new Google Sheet action
  // inputs: OptionBase<{ type: string }>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputs: any;
};

export type SurveyAction = BaseAction<'survey'> & {
  url: string;
  name: string;
  label: string;
  height: string;
};

export type SetVariablesAction = BaseAction<'set_variables'> & {
  node_id: string;
  variables: { variable: string; value: string }[];
};

export type QuestionAction = { action_id: string; type: 'question' };

export type ReminderAction = BaseAction<'reminder'> & {
  reminder_seconds: number;
  trigger_node_id: string;
};

export type WhatsAppFlowActionPayload = { screen: string; data?: Record<string, unknown> };

export type WhatsAppFlowConfig = {
  flow_id: string;
  flow_body: string;
  flow_cta: string;
  flow_action: 'navigate' | 'data_exchange';
  flow_header?: string;
  flow_footer?: string;
  flow_action_payload?: WhatsAppFlowActionPayload;
  flow_mode?: 'draft';
};

/**
 * Partial WhatsApp Flow config for display purposes.
 * Used when rendering flow messages where fields may be absent.
 */
export type WhatsAppFlowDisplay = Partial<WhatsAppFlowConfig>;

export type WhatsAppFlowAction = BaseAction<'whatsapp_flow'> & {
  whatsapp_flow: WhatsAppFlowConfig;
  trigger_node_id?: string;
};

export type QuestionType = 'single' | 'multi' | 'text';

/** Shows a question or an option only when an earlier choice question got one of `in`. */
export type QuestionCondition = { key: string; in: string[] };

export type QuestionOption = {
  label: string;
  value: string;
  description?: string;
  recommended?: boolean;
  show_if?: QuestionCondition;
};

export type Question = {
  key: string;
  header: string;
  question: string;
  type: QuestionType;
  options?: QuestionOption[];
  allow_other?: boolean;
  show_if?: QuestionCondition;
};

export type QuestionSetConfig = { title: string; questions: Question[] };

export type QuestionSetAction = BaseAction<'question_set'> & {
  question_set: QuestionSetConfig;
  trigger_node_id?: string;
};

/**
 * Auth variant of the sub_agent action's nested config. The `type` field is
 * the discriminator (mirrors the orchestrator's `SubAgentConfig` union and the
 * Python `SubAgentConfigUnion` in the dialog runtime).
 */
export type AuthSubAgentConfig = AuthConfig & { type: 'auth' };

/**
 * AI Response variant of the sub_agent action's nested config. Invokes the
 * Synthesis stack scoped by `guidelines` (smaller-scope instructions). The
 * `type` field is the discriminator (mirrors the orchestrator's
 * `SubAgentConfig` union and the Python `SubAgentConfigUnion`).
 */
export type AIResponseSubAgentConfig = { type: 'ai_response'; guidelines?: string };

/**
 * Discriminated union of all sub-agent configs that may be nested under a
 * `sub_agent` action's `sub_agent_config` field. Extend when adding new
 * sub-agent types.
 */
export type SubAgentConfig = AuthSubAgentConfig | AIResponseSubAgentConfig;

export type SubAgentAction = BaseAction<'sub_agent'> & { sub_agent_config: SubAgentConfig };

export type Action =
  | TextAction
  | HandoverAction
  | UrlAction
  | ImageAction
  | VideoAction
  | FileAction
  | CarouselAction
  | WebviewAction
  | ResetAction
  | WebhookAction
  | TagAction
  | EventAction
  | PauseAction
  | EmailAction
  | GooglesheetAction
  | CloseAction
  | SwitchChannelAction
  | SurveyAction
  | ReplayAction
  | SetVariablesAction
  | QuestionAction
  | ReminderAction
  | WhatsAppFlowAction
  | QuestionSetAction
  | SubAgentAction;

type ButtonBase = OptionBase<{ id?: string; hasDuplicatedLabels?: boolean }>;
type AsUrl = { type: 'url'; url: string };
type AsPostback = { type: 'postback'; value: string };
type AsPhone = { type: 'phone'; value: string };
type AsWebview = {
  type: 'webview';
  url: string;
  trigger_node_id: string;
  height?: 'tall' | 'compact' | 'full';
  auto_open?: boolean;
};

export type CarouselButton = ButtonBase & (AsUrl | AsPostback | AsPhone | AsWebview);

export type CarouselCard = {
  title: string;
  subtitle: string;
  media?: { type: 'image' | 'video'; url: string };
  default_action?: CarouselButton;
  buttons?: CarouselButton[];
};

export type TagOperation = 'add' | 'remove' | 'clear';

export type ChannelPassthrough = { channel: IntegrationType; payload: string };
